import * as React from "react";
import Users from "lucide-react/icons/users";
import Wrench from "lucide-react/icons/wrench";
import BarChart3 from "lucide-react/icons/bar-chart-3";
import Rocket from "lucide-react/icons/rocket";
import BookOpen from "lucide-react/icons/book-open";
import Settings from "lucide-react/icons/settings";
import Award from "lucide-react/icons/award";
import Zap from "lucide-react/icons/zap";
import { useTranslation } from "react-i18next";

/**
 * WhatsIncluded Component - Redesigned
 *
 * A grid-based component showcasing the book's core value propositions:
 * - What You Will Learn: Core learning objectives and skills
 * - Why This Book?: Unique differentiators and approach
 *
 * Features:
 * - Clean grid layout with prominent Lucide icons
 * - No tabs - all content visible at once
 * - Responsive design optimized for mobile and desktop
 * - Eye-catching icons that draw attention to key benefits
 *
 * Note: Removed "What's Included" section as it's redundant with PricingTiers
 *
 * @example
 * // Basic usage with default content
 * <WhatsIncluded />
 *
 * @example
 * // Compact mode for space-constrained layouts
 * <WhatsIncluded compact={true} />
 */

interface WhatsIncludedProps {
  /** Whether to show the section in compact mode */
  compact?: boolean;
  /** Optional custom styling classes */
  className?: string;
}

// Icon mapping for benefits
const learningIcons = [Users, Wrench, BarChart3, Rocket];
const differentiatorIcons = [BookOpen, Settings, Award, Zap];

const WhatsIncluded: React.FC<WhatsIncludedProps> = ({
  compact = false,
  className = "",
}) => {
  const { t } = useTranslation("home");
  const containerClasses = compact ? "mb-4" : "mb-6";

  // Translation keys for why this book section
  const whyThisBookKeys = [
    "theoryAndPractice",
    "frameworkAgnostic",
    "evaluationFocus",
    "endToEndApproach",
  ];

  // Translation keys for what you will learn section
  const whatYouWillLearnKeys = [
    "multiAgentFundamentals",
    "buildFromScratch",
    "evaluationOptimization",
    "realWorldApplications",
  ];

  return (
    <div className={`${containerClasses} ${className}`}>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Box: Why This Book? */}
        <div className="bg-primary/30 border border-secondary/30 rounded-lg p-4 hover:border-accent/40 transition-all duration-200">
          <div className="flex items-center space-x-3 mb-3">
            <div className="flex-shrink-0 bg-accent/10 rounded-lg p-2">
              <Award className="w-6 h-6 text-accent" strokeWidth={1.5} />
            </div>
            <h2 className="text-lg font-bold text-primary">{t("whyThisBook.title")}</h2>
          </div>
          <ul className="space-y-3">
            {whyThisBookKeys.map((key, index) => {
              const Icon = differentiatorIcons[index];
              return (
                <li key={index} className="flex items-start">
                  <span className="text-accent mr-3 text-base flex-shrink-0 mt-0.5">
                    •
                  </span>
                  <span className="text-secondary text-base leading-relaxed">
                    <strong className="text-primary">{t(`whyThisBook.${key}.title`)}</strong> —{" "}
                    {t(`whyThisBook.${key}.description`)}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Right Box: What You Will Learn */}
        <div className="bg-primary/30 border border-secondary/30 rounded-lg p-4 hover:border-accent/40 transition-all duration-200">
          <div className="flex items-center space-x-3 mb-3">
            <div className="flex-shrink-0 bg-accent/10 rounded-lg p-2">
              <Users className="w-6 h-6 text-accent" strokeWidth={1.5} />
            </div>
            <h2 className="text-lg font-bold text-primary">
              {t("whatYouWillLearn.title")}
            </h2>
          </div>
          <ul className="space-y-3">
            {whatYouWillLearnKeys.map((key, index) => {
              const Icon = learningIcons[index];
              return (
                <li key={index} className="flex items-start">
                  <span className="text-accent mr-3 text-base flex-shrink-0 mt-0.5">
                    •
                  </span>
                  <span className="text-secondary text-base leading-relaxed">
                    <strong className="text-primary">{t(`whatYouWillLearn.${key}.title`)}</strong> —{" "}
                    <span dangerouslySetInnerHTML={{ __html: t(`whatYouWillLearn.${key}.description`) }} />
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default WhatsIncluded;
